<script>
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {useEvaluationPhaseStore} from "@/stores/Evaluation/EvaluationPhase";
import {computed, onMounted, ref} from "vue";
import axios from "axios";
import useSharedSelectData from "@/use/SharedSelectData";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
        type: String,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);

        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {evaluationEvaluatorTypes} = useSharedSelectData();
        const {getBack} = useSharedMethod();
        const {$refs, $route} = useTools();

        const evaluationPhaseStore = useEvaluationPhaseStore();

        const evaluationPhase = ref({initFromPrecedent: false});

        function findById(id) {
            loading.value = true;
            axios.get(`/evaluationPhases/${id}`).then((response) => {
                evaluationPhase.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }


        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                evaluationPhase.value.type = props.type;
                axios.post('/evaluationPhases', evaluationPhase.value).then((response) => {
                    evaluationPhaseStore.addEvaluationPhase(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                evaluationPhase.value.type = props.type;
                axios.put('/evaluationPhases', evaluationPhase.value).then((response) => {
                    evaluationPhaseStore.updateEvaluationPhase(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/evaluationPhases/${evaluationPhase.value.id}`).then(() => {
                    evaluationPhaseStore.deleteEvaluationPhase(evaluationPhase.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        onMounted(() => {
            if (!props.addAction)
                findById($route.params.id);
        });

        return {
            evaluationPhaseStore,
            mxRules,
            readonly,
            dialog,
            loading,
            overlay,
            evaluationPhase,
            evaluationEvaluatorTypes,
            save,
            update,
            remove,
            getBack,
        };
    }
}
</script>

<template>
    <v-dialog
        v-model="dialog"
        max-width="800"
        persistent
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :view-action="viewAction"
                :delete-action="deleteAction"
                :add-action="addAction"
                :update-action="updateAction"
            ></w-dialog-toolbar>
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>
            <v-card-text>
                <v-container grid-list-md>
                    <v-form ref="form">
                        <w-fieldset :title="$t('common.general')">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('common.label')" required/>
                                    <w-text-field
                                        v-model="evaluationPhase.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('evaluation.evaluator')" required/>
                                    <w-enum-select
                                        v-model="evaluationPhase.evaluator"
                                        :items="evaluationEvaluatorTypes"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" v-if="evaluationPhase.evaluator === 'SPECIFIC'">
                                    <w-label :label="$t('module.worker')" required/>
                                    <str-worker-autocomplete
                                        v-model="evaluationPhase.workerId"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-checkbox
                                        v-model="evaluationPhase.initFromPrecedent"
                                        :readonly="readonly"
                                        hide-details
                                    >
                                        <template #label>
                                            <strong>{{ $t('evaluation.init_n-1') }}</strong>
                                        </template>
                                    </v-checkbox>
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.comment')"/>
                                    <w-simple-text-area
                                        v-model="evaluationPhase.notes"
                                        :readonly="readonly"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>